import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';

import MemberConfigurationRoles from 'services/product/license-group/member-configuration/MemberConfigurationRoles';
import LicenseGroupUserList from 'services/product/license-group/user-list/LicenseGroupUserList';

const getProductRoleId = (member, {licenseGroupId, productId}) =>
  member.productRoles.find(
    (role) => role.product.id === productId && role.licenseGroupId === licenseGroupId
  )?.id;

// Transform a list of members to Member Configuration Roles
//
// [
//    Member({
//       id: 'user-1-id'
//       productRoles: [{id: 'user-1-role-1', product: {id: 'product-1-id'}}, {id: 'user-1-role-2'}, product: {id: 'product-2-id'}]
//    }),
//    Member({
//       id: 'user-2-id'
//       productRoles: [{id: 'user-2-role', product: {id: 'product-1-id'}]
//    })
// ]
//
// => result for product id: 'product-1-id'
// => {
//       'user-1-id': ['user-1-role-1'],
//       'user-2-id': ['user-2-role'],
//    }
const toMemberConfigurationRoles = (members, {licenseGroupId, productId}) =>
  members.reduce(
    (result, member) => ({
      ...result,
      [member.id]: [getProductRoleId(member, {licenseGroupId, productId})],
    }),
    {}
  );

const registerProductRoles = (members, {licenseGroupId, productId}) => {
  members.forEach((member) => {
    Object.assign(member, {
      productRole: [getProductRoleId(member, {licenseGroupId, productId})],
    });
  });
};

/**
 * Save member configuration roles for the given members based on user input stored in the model
 * @param {Array<Member>} members - members
 */
const updateMemberConfigurationRoles = async (members, {licenseGroupId, orgId, productId}) => {
  const memberConfigurationRoles = new MemberConfigurationRoles({
    licenseGroupId,
    memberIds: [],
    orgId,
    productId,
  });
  await memberConfigurationRoles.refresh();
  memberConfigurationRoles.memberRoles = toMemberConfigurationRoles(members, {
    licenseGroupId,
    productId,
  });
  await memberConfigurationRoles.save();
};

/**
 * Save member product roles for the given members based on user input stored in the model
 * @param {Array<Member>} members - members
 */
const updateMemberProductRoles = async (members, {licenseGroupId, orgId, productId}) => {
  const licenseGroupUserList = new LicenseGroupUserList({licenseGroupId, orgId, productId});
  registerProductRoles(members, {licenseGroupId, productId});
  await licenseGroupUserList.saveMemberProductRole(members);
};

const getUpdateProductRoleFunc = (product) =>
  product.hasConfigurationSettingForLicenseGroupMember()
    ? updateMemberConfigurationRoles
    : updateMemberProductRoles;

/**
 * Save members' product roles for the current org
 * @param {Array<Member>} members - a list of members
 * @param {String} orgId - the org id
 * @returns {Promise} Resolves when updates are successful
 */
const saveMemberProductRoles = (members, {orgId}) => {
  const existingMembers = members.filter((member) => !member.isNew());
  // get flatten product roles for org users
  const membersProductRoles = flatMap(existingMembers, (member) => {
    const productRoles = flatMap(member.productRoles, (productRole) => ({
      ...productRole,
      member,
    }));

    return productRoles;
  });

  const productRolesGroupedByLicenseGroup = Object.values(
    groupBy(
      membersProductRoles,
      (productRole) => `${productRole.product.id}_${productRole.licenseGroupId}`
    )
  );

  return Promise.all(
    productRolesGroupedByLicenseGroup.map((productRolesForLicenseGroup) => {
      const product = productRolesForLicenseGroup[0].product;
      const licenseGroupId = productRolesForLicenseGroup[0].licenseGroupId;
      const membersWithProductRolesForLicenseGroupId = productRolesForLicenseGroup.map(
        (productRole) => productRole.member
      );

      return getUpdateProductRoleFunc(product)(membersWithProductRolesForLicenseGroupId, {
        licenseGroupId,
        orgId,
        productId: product.id,
      });
    })
  );
};

// eslint-disable-next-line import/prefer-default-export -- helper file
export {saveMemberProductRoles};
